class ShFacetDrawer extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this.mainDetails = this.querySelector('details');
    this.opener = this.mainDetails.querySelector(':scope > summary');
    this.panel = this.querySelector('[role="dialog"]');
    this.opener.setAttribute('aria-haspopup', 'dialog');
    this.opener.setAttribute('aria-expanded', String(this.mainDetails.open));
    this.mainDetails.addEventListener('toggle', () => {
      this.opener.setAttribute('aria-expanded', String(this.mainDetails.open));
      this.mainDetails.classList.toggle('menu-opening', this.mainDetails.open);
      document.body.classList.toggle('overflow-hidden-mobile', this.mainDetails.open);
      if (this.mainDetails.open) {
        this.panel.querySelectorAll('details[open]').forEach(group => { group.open = false; });
        trapFocus(this.panel, this.querySelector('.sh-facets-close'));
      }
      else removeTrapFocus(this.opener);
    });
    this.addEventListener('click', (event) => {
      if (event.target.closest('.sh-facets-close, [data-sh-view-results]')) this.close();
      if (event.target.classList.contains('mobile-facets')) this.close();
    });
    this.addEventListener('keyup', (event) => {
      if (event.key !== 'Escape') return;
      event.stopImmediatePropagation();
      this.close();
    }, true);
    this.addEventListener('toggle', (event) => {
      if (event.target === this.mainDetails) return;
      event.target.querySelector(':scope > summary')?.setAttribute('aria-expanded', String(event.target.open));
    }, true);
    this.viewport = matchMedia('(min-width: 990px)');
    this.onViewport = () => { if (this.viewport.matches && !this.classList.contains('sh-desktop-drawer')) this.close(); };
    this.viewport.addEventListener('change', this.onViewport);
  }

  close() {
    if (!this.mainDetails.open) return;
    this.mainDetails.open = false;
    this.mainDetails.classList.remove('menu-opening');
    this.opener.setAttribute('aria-expanded', 'false');
    document.body.classList.remove('overflow-hidden-mobile');
    removeTrapFocus(this.opener);
  }

  bindEvents() {}

  disconnectedCallback() {
    this.viewport?.removeEventListener('change', this.onViewport);
    if (this.mainDetails?.open) {
      document.body.classList.remove('overflow-hidden-mobile');
      removeTrapFocus();
    }
  }
}
customElements.define('sh-facet-drawer', ShFacetDrawer);

class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', (event) => {
      if (!FacetFiltersForm.catalogReady) {
        const original = event.target;
        const action = {
          name: original.name,
          type: original.type,
          value: original.value,
          size: original.dataset.shSize,
          checked: original.checked,
        };
        FacetFiltersForm.initializeFilterCatalog().then(() => {
          const replacement = Array.from(facetForm.elements).find(input =>
            input.name === action.name && (action.type !== 'checkbox' ||
              (action.size ? input.dataset.shSize === action.size : input.value === action.value)),
          );
          if (!replacement) return;
          if (action.type === 'checkbox') replacement.checked = action.checked;
          else replacement.value = action.value;
          replacement.dispatchEvent(new Event('input', { bubbles: true }));
        }).catch(error => console.error(error));
        return;
      }
      if (event.target.matches('[data-sh-size]') && event.target.checked) {
        const available = facetForm.querySelector('input[name="filter.v.availability"][value="1"]');
        if (available) available.checked = true;
      }
      clearTimeout(FacetFiltersForm.submitTimer);
      ++FacetFiltersForm.requestId;
      FacetFiltersForm.controller?.abort();
      FacetFiltersForm.setPending(true);
      if (event.target.matches('price-range input')) {
        FacetFiltersForm.submitTimer = setTimeout(() => this.onSubmitHandler(event), 300);
      } else {
        this.onSubmitHandler(event);
      }
    });

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static initializeFilterCatalog() {
    if (FacetFiltersForm.catalogPromise) return FacetFiltersForm.catalogPromise;
    const activeFilters = [...new URLSearchParams(location.search)].some(
      ([name, value]) => name.startsWith('filter.') && value !== '',
    );
    if (!activeFilters) {
      FacetFiltersForm.captureFilterCatalog(document);
      FacetFiltersForm.catalogReady = true;
      FacetFiltersForm.catalogPromise = Promise.resolve();
      FacetFiltersForm.restoreFilterOptions(document, location.search);
      return FacetFiltersForm.catalogPromise;
    }
    FacetFiltersForm.setPending(true);
    const url = new URL(location.href);
    [...url.searchParams.keys()].forEach(name => {
      if (name.startsWith('filter.') || name === 'page' || name === 'sort_by') url.searchParams.delete(name);
    });
    url.searchParams.set('section_id', FacetFiltersForm.getSections()[0].section);
    FacetFiltersForm.catalogPromise = fetch(url.pathname + url.search)
      .then(response => {
        if (!response.ok) throw new Error('Filter options request failed');
        return response.text();
      })
      .then(html => {
        const source = new DOMParser().parseFromString(html, 'text/html');
        FacetFiltersForm.captureFilterCatalog(source);
        FacetFiltersForm.catalogReady = true;
        FacetFiltersForm.restoreFilterOptions(document, location.search);
        FacetFiltersForm.setPending(false);
      })
      .catch(error => {
        FacetFiltersForm.catalogPromise = null;
        FacetFiltersForm.setPending(false);
        document.querySelector('.sh-facets-error')?.classList.remove('hidden');
        throw error;
      });
    return FacetFiltersForm.catalogPromise;
  }

  static captureFilterCatalog(root) {
    FacetFiltersForm.filterCatalog = ['#FacetsWrapperDesktop', '#FacetsWrapperMobile'].map(selector => ({
      selector,
      facets: Array.from(root.querySelectorAll(`${selector} > .js-filter`), node => node.cloneNode(true)),
    }));
    FacetFiltersForm.activeFacetTemplate = root.querySelector('[data-sh-active-facet-template]')?.content.cloneNode(true);
  }

  static restoreFilterOptions(root, searchParams) {
    const params = new URLSearchParams(searchParams);
    const activeId = root === document ? document.activeElement?.id : null;
    for (const { selector, facets } of FacetFiltersForm.filterCatalog || []) {
      const wrapper = root.querySelector(selector);
      if (!wrapper) continue;
      let previous = null;
      for (const template of facets) {
        const current = root.getElementById(template.id);
        const isPrice = Boolean(template.querySelector('price-range'));
        const facet = (isPrice && current ? current : template).cloneNode(true);
        for (const input of facet.querySelectorAll('input[type="checkbox"]')) {
          const values = params.getAll(input.name);
          const checked = input.dataset.shSize
            ? values.some(value => value.trim().split(/\s+/)[0] === input.dataset.shSize)
            : values.includes(input.value);
          input.checked = checked;
          input.toggleAttribute('checked', checked);
          input.disabled = false;
          const label = input.closest('label');
          label?.classList.remove('disabled');
          label?.removeAttribute('aria-disabled');
          label?.classList.toggle('active', checked);
        }
        for (const input of facet.querySelectorAll('price-range input')) {
          input.value = params.get(input.name) || '';
          input.setAttribute('value', input.value);
        }
        const filterName = facet.querySelector('input[name]')?.name;
        if (filterName) {
          const resetParams = new URLSearchParams(params);
          resetParams.delete(filterName);
          if (isPrice) {
            resetParams.delete('filter.v.price.gte');
            resetParams.delete('filter.v.price.lte');
          }
          for (const reset of facet.querySelectorAll('a.facets__reset')) {
            reset.setAttribute('href', location.pathname + (resetParams.size ? '?' + resetParams : ''));
          }
        }
        if (current) {
          current.innerHTML = facet.innerHTML;
          previous = current;
        } else {
          if (previous) previous.after(facet);
          else {
            const heading = wrapper.querySelector(':scope > .facets__heading');
            if (heading) heading.after(facet);
            else wrapper.prepend(facet);
          }
          previous = facet;
        }
      }
    }
    FacetFiltersForm.restoreActiveOptions(root, params);
    if (activeId && root === document) document.getElementById(activeId)?.focus({ preventScroll: true });
  }

  static restoreActiveOptions(root, params) {
    if (!FacetFiltersForm.activeFacetTemplate) return;
    const catalog = FacetFiltersForm.filterCatalog.find(group => group.facets.length)?.facets || [];
    const selected = [];
    for (const facet of catalog) {
      const title = (
        facet.querySelector('summary')?.getAttribute('aria-label') ||
        facet.querySelector('.sh-facet-title, .facets__summary-label')?.textContent || ''
      ).trim();
      for (const input of facet.querySelectorAll('input[type="checkbox"]')) {
        const values = params.getAll(input.name);
        const active = input.dataset.shSize
          ? values.some(value => value.trim().split(/\s+/)[0] === input.dataset.shSize)
          : values.includes(input.value);
        if (!active) continue;
        const label = input.closest('label');
        const text = (
          label?.querySelector('.facet-checkbox__text-label, .facet-checkbox__text')?.textContent ||
          label?.querySelector('span:last-child')?.textContent || input.value
        ).trim();
        const remaining = new URLSearchParams(params);
        remaining.delete(input.name);
        for (const value of values) {
          const remove = input.dataset.shSize
            ? value.trim().split(/\s+/)[0] === input.dataset.shSize
            : value === input.value;
          if (!remove) remaining.append(input.name, value);
        }
        selected.push({ name: input.name, size: input.dataset.shSize, text: title ? `${title}: ${text}` : text, remaining });
      }
    }
    for (const container of root.querySelectorAll('.active-facets-mobile, .active-facets-desktop')) {
      container.querySelectorAll('facet-remove').forEach(node => {
        if (node.querySelector('[data-sh-filter-name], [data-sh-size-remove]')) node.remove();
      });
      const fragment = root.createDocumentFragment();
      for (const option of selected) {
        const pill = FacetFiltersForm.activeFacetTemplate.cloneNode(true);
        const link = pill.querySelector('a');
        link.dataset.shFilterName = option.name;
        if (option.size) link.dataset.shSizeRemove = option.size;
        link.setAttribute('href', location.pathname + (option.remaining.size ? '?' + option.remaining : ''));
        pill.querySelector('[data-sh-filter-text]').textContent = option.text;
        fragment.append(pill);
      }
      container.prepend(fragment);
    }
    const priceSelected = ['filter.v.price.gte', 'filter.v.price.lte'].some(name => params.get(name));
    const activeCount = selected.length + Number(priceSelected);
    for (const badge of root.querySelectorAll('.sh-facet-badge')) {
      badge.textContent = activeCount;
      badge.classList.toggle('hidden', activeCount === 0);
    }
  }

  static setPending(pending) {
    document.querySelectorAll('.sh-view-results').forEach(button => {
      button.disabled = pending;
      button.setAttribute('aria-busy', String(pending));
    });
    document.querySelector('.sh-facets-error')?.classList.add('hidden');
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    if (document.querySelector('.facets-container')?.dataset.template === 'collection') {
      const parameters = new URLSearchParams(searchParams);
      parameters.set('filter.v.availability', '1');
      searchParams = parameters.toString();
    }
    if (!FacetFiltersForm.catalogReady) {
      const pendingId = ++FacetFiltersForm.requestId;
      FacetFiltersForm.setPending(true);
      FacetFiltersForm.initializeFilterCatalog().then(() => {
        if (pendingId === FacetFiltersForm.requestId) FacetFiltersForm.renderPage(searchParams, event, updateURLHash);
      }).catch(error => console.error(error));
      return;
    }
    clearTimeout(FacetFiltersForm.submitTimer);
    FacetFiltersForm.controller?.abort();
    FacetFiltersForm.controller = new AbortController();
    const requestId = ++FacetFiltersForm.requestId;
    FacetFiltersForm.setPending(true);
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const updateEvent = FacetFiltersForm.startUpdateEvent(searchParams);
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner',
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
    document.getElementById('ProductGridContainer').querySelector('.collection').classList.add('loading');
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event, updateEvent, requestId)
        : FacetFiltersForm.renderSectionFromFetch(url, event, updateEvent, requestId);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static startUpdateEvent(searchParams) {
    const { SearchUpdateEvent, CollectionUpdateEvent } = window.StandardEvents || {};
    if (!SearchUpdateEvent && !CollectionUpdateEvent) return;

    const facetsForm = document.querySelector('facet-filters-form');
    const facetsContainer = document.querySelector('.facets-container');
    const collectionId = facetsContainer?.dataset.collectionId || null;
    const collectionHandle = facetsContainer?.dataset.collectionHandle || '';
    const currentCount = parseInt(document.getElementById('ProductCount')?.dataset.productCount) || 0;
    const urlSearchParams = new URLSearchParams(searchParams);
    const isSearchPage = facetsContainer?.dataset.template === 'search';
    const isCollectionPage = facetsContainer?.dataset.template === 'collection';
    const dispatchTarget = facetsForm || document;

    let deferred;

    if (isSearchPage) {
      deferred = SearchUpdateEvent.createPromise();
      dispatchTarget.dispatchEvent(
        new SearchUpdateEvent({
          search: {
            query: urlSearchParams.get('q') || '',
            productFilters: SearchUpdateEvent.parseProductFilters(urlSearchParams),
            sortKey: SearchUpdateEvent.getSortKey(urlSearchParams),
          },
          promise: deferred.promise,
        }),
      );
    } else if (isCollectionPage) {
      // collectionId is null for auto-generated collections (/collections/all,
      // /collections/vendors/<x>, etc.) — the SSE schema accepts that.
      deferred = CollectionUpdateEvent.createPromise();
      dispatchTarget.dispatchEvent(
        new CollectionUpdateEvent({
          collection: { id: collectionId, handle: collectionHandle, productsCount: currentCount },
          productFilters: CollectionUpdateEvent.parseProductFilters(urlSearchParams),
          sortKey: CollectionUpdateEvent.getSortKey(urlSearchParams),
          promise: deferred.promise,
        }),
      );
    }

    if (!deferred) return;

    return {
      resolve: (filteredCount) => {
        deferred.resolve({ [isSearchPage ? 'totalCount' : 'productsCount']: filteredCount });
      },
      reject: deferred.reject,
    };
  }

  static renderSectionFromFetch(url, event, updateEvent, requestId) {
    fetch(url, { signal: FacetFiltersForm.controller.signal })
      .then((response) => {
        if (!response.ok) throw new Error('Filter request failed');
        return response.text();
      })
      .then((html) => {
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        if (requestId === FacetFiltersForm.requestId) FacetFiltersForm.renderSection(html, event, updateEvent);
        else updateEvent?.resolve(0);
      })
      .catch((error) => {
        if (error.name === 'AbortError' || requestId !== FacetFiltersForm.requestId) {
          updateEvent?.resolve(0);
          return;
        }
        if (requestId === FacetFiltersForm.requestId) {
          FacetFiltersForm.setPending(false);
          document.querySelector('.sh-facets-error')?.classList.remove('hidden');
          document.querySelector('#ProductGridContainer .collection')?.classList.remove('loading');
          document.querySelectorAll('.facets-container .loading__spinner').forEach(el => el.classList.add('hidden'));
        }
        console.error(error);
        updateEvent?.reject(error);
      });
  }

  static renderSectionFromCache(filterDataUrl, event, updateEvent, requestId) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    if (requestId === FacetFiltersForm.requestId) FacetFiltersForm.renderSection(html, event, updateEvent);
  }

  static renderSection(html, event, updateEvent) {
    const scrollTop = window.scrollY;
    const panel = document.querySelector('.sh-facets .mobile-facets__main');
    const panelScrollTop = panel?.scrollTop;
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    FacetFiltersForm.restoreFilterOptions(parsedHTML, FacetFiltersForm.searchParamsPrev);
    FacetFiltersForm.renderFilters(parsedHTML, event);
    FacetFiltersForm.renderProductGridContainer(parsedHTML);
    FacetFiltersForm.renderProductCount(parsedHTML, updateEvent);
    FacetFiltersForm.setPending(false);
    if (event) window.scrollTo({ top: scrollTop, behavior: 'instant' });
    if (panel) panel.scrollTop = panelScrollTop;
    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
  }

  static renderProductGridContainer(html) {
    document.getElementById('ProductGridContainer').innerHTML = html.getElementById('ProductGridContainer').innerHTML;

    document
      .getElementById('ProductGridContainer')
      .querySelectorAll('.scroll-trigger')
      .forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });
  }

  static renderProductCount(html, updateEvent) {
    const sourceCount = html.getElementById('ProductCount');
    const count = sourceCount.innerHTML;
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    container.innerHTML = count;
    container.dataset.productCount = sourceCount.dataset.productCount || '';
    container.dataset.totalCount = sourceCount.dataset.totalCount || '';
    container.classList.remove('loading');
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner',
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));

    updateEvent?.resolve(parseInt(sourceCount.dataset.productCount) || 0);
  }

  static renderFilters(parsedHTML, event) {
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter',
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter',
    );

    // Remove facets that are no longer returned from the server
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      // Element already rendered in the DOM so just update the innerHTML
      if (currentElement) {
        document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          // Same facet type (eg horizontal/vertical or drawer/mobile)
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          document.querySelector(`#${elementToRender.parentElement.id} .js-filter`).before(elementToRender);
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) {
      const closestJSFilterID = event.target.closest('.js-filter').id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));

        const newFacetDetailsElement = document.getElementById(closestJSFilterID);

        const isTextInput = event.target.getAttribute('type') === 'text';

        if (!isTextInput) {
          // Try to return focus to the same checkbox the user just toggled,
          // re-selecting it from the freshly rendered HTML by its id.
          const originatingInputId = event.target.id;
          const matchingInput = originatingInputId
            ? newFacetDetailsElement.querySelector(`#${CSS.escape(originatingInputId)}`)
            : null;

          if (matchingInput) {
            matchingInput.focus({ preventScroll: true });
          } else {
            // Fallback to summary/close button if the checkbox can't be found
            const fallbackSelector = newFacetDetailsElement.classList.contains('mobile-facets__details')
              ? `.mobile-facets__summary`
              : `.facets__summary`;
            const fallbackElement = newFacetDetailsElement.querySelector(fallbackSelector);
            if (fallbackElement) fallbackElement.focus();
          }
        }
      }
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting', '#FacetSortMobileForm', '.sh-view-results'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });

    document.getElementById('FacetFiltersFormMobile').closest('menu-drawer, sh-facet-drawer')?.bindEvents();
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const sourceSummary = source.querySelector('.mobile-facets__summary');
    const targetSummary = target.querySelector('.mobile-facets__summary');
    if (sourceSummary && targetSummary) targetSummary.innerHTML = sourceSummary.innerHTML;
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    const searchParams = new URLSearchParams(formData);
    const sizeValues = searchParams.getAll('filter.v.option.talla').flatMap(value => value.split(','));
    searchParams.delete('filter.v.option.talla');
    [...new Set(sizeValues)].forEach(value => searchParams.append('filter.v.option.talla', value));



    if (form.id === 'FacetFiltersFormMobile') {
      const mobileSort = document.getElementById('ShSortByMobile');
      if (mobileSort) searchParams.set('sort_by', mobileSort.value);
    }
    return searchParams.toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    if (event.target.closest('form').id === 'FacetSortMobileForm') {
      this.onSubmitForm(this.createSearchParams(document.getElementById('FacetFiltersFormMobile')), event);
      return;
    }
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    if (event.srcElement.className == 'mobile-facets__checkbox') {
      const searchParams = this.createSearchParams(event.target.closest('form'));
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];
      const isMobile = event.target.closest('form').id === 'FacetFiltersFormMobile';

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join('&'), event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    if (event.currentTarget.dataset.shSizeRemove) {
      const params = new URLSearchParams(window.location.search);
      const size = event.currentTarget.dataset.shSizeRemove;
      const values = params.getAll('filter.v.option.talla').flatMap(value => value.split(','));
      const remaining = values.filter(value => value.trim().split(/\s+/)[0] !== size);
      params.delete('filter.v.option.talla');
      remaining.forEach(value => params.append('filter.v.option.talla', value));
      FacetFiltersForm.renderPage(params.toString());
      return;
    }
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.requestId = 0;
FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => FacetFiltersForm.initializeFilterCatalog().catch(error => console.error(error)), { once: true });
} else {
  FacetFiltersForm.initializeFilterCatalog().catch(error => console.error(error));
}

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach((element) => {
      element.addEventListener('change', this.onRangeChange.bind(this));
      element.addEventListener('keydown', this.onKeyDown.bind(this));
    });
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  onKeyDown(event) {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', 0);
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('data-min'));
    const max = Number(input.getAttribute('data-max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);



document.addEventListener('click', (event) => {
  document.querySelectorAll('.sh-facets #FacetsWrapperDesktop > details[open]').forEach(details => {
    if (!details.contains(event.target)) details.open = false;
  });
});
